// The hover card behind a Step 2 cell: what each of its steps shows and rests on. The
// heatmap opens it on hover; the edit pane shows the same card for the step it edits. Text
// selected on a step is a comment for the specialist whose record it reads, and on an item
// needed from Amazon one for the item's keeper, as on Step 3
// (docs/Y2026-Q4/design-amazon-heatmap-comments.md).
import { Fragment, type ReactNode } from "react";

import type {
  AmazonBuilding,
  AmazonNeed,
  BuildingStep,
} from "../../api/hooks/useAmazonOverview";
import { Chevron } from "../../components/Chevron";
import { firstSentence } from "./cells";
import { day, formatDate, today } from "./dates";
import { HoldTag } from "./HoldTag";
import { GapTag } from "../../components/GapTag";
import { reviewAttrs } from "../../utils/reviewState";
import { markOf, useEdit } from "./editContext";
import { cellKeys } from "./overrides";
import { blocksItsStep, itemReviewAttrs, neededRuns, owed, stillNeededText } from "./needs";
import { stepById } from "./pathSteps";
import { itemNeededBy, neededByReason } from "./schedule";
import { PartyChip } from "./PartyChip";
import { type CardLine, cardTexts, proofOf, type Shown, TONE_CLASS } from "./shownPart";
import styles from "./amazonOverview.module.css";

/** What a dated step rests on (``proofOf``), as the card shows it. */
const Proof = ({ id, step }: { id: string; step: BuildingStep }) => {
  const proof = proofOf(id, step);
  if (!proof) return null;
  return <p className={proof.means ? styles.cardMeans : styles.cardProof}>{proof.text}</p>;
};

/** What the step needs from Amazon, as one list in Step 3's card's order (``neededRuns``):
 * its items and its open feedback points together, still to come first, by category, each
 * saying whether it holds the step up (why, on hover) and, once past its date, that it is
 * late and why that date (Noaa, 19 Oct 2026). */
const NeededList = ({
  b,
  part,
}: {
  b: AmazonBuilding;
  part: Shown & { items: AmazonNeed[] };
}) => {
  const now = today();
  const { runs } = neededRuns(part.items, part.id, (id) => stepById(id).title);
  return (
    <details className={styles.cardMore}>
      <summary>
        Needed from Amazon ({part.items.length}) · {stillNeededText(part.items)} <Chevron />
      </summary>
      {runs.map(({ run, groups }) => (
        <Fragment key={run.label}>
          {runs.length > 1 && <p className={styles.cardListHead}>{run.label}</p>}
          {groups.map(([category, items]) => (
            <Fragment key={category}>
              {category && (
                <p className={styles.cardCategoryHead}>
                  {category} ({items.length})
                </p>
              )}
              <ol>
                {items.map((n) => {
                  const by = owed(n) ? itemNeededBy(b, n, part.need) : null;
                  return (
                    <li key={n.key} {...itemReviewAttrs(b.site_code, n)}>
                      {n.text}
                      {n.status === "received" && n.date && ` · ${formatDate(day(n.date))}`}
                      {n.status === "partial" && n.partial && (
                        <GapTag partial={n.partial} heading="Why it is partially received" />
                      )}
                      {owed(n) &&
                        (blocksItsStep(n) ? (
                          <HoldTag need={n} label="Holds its step up" />
                        ) : (
                          <span className={styles.freeTag}>Doesn&apos;t hold it up</span>
                        ))}
                      {by && by < now && (
                        <span className={styles.heldWhy}>
                          Late: needed by {formatDate(by)},{" "}
                          {neededByReason(n, (id) => stepById(id).title).toLowerCase()}.
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            </Fragment>
          ))}
        </Fragment>
      ))}
    </details>
  );
};

/** How the edit pane tags a card line: its letter, and whether unsaved changes alter it. */
export interface LineMark {
  letter: string;
  changed: boolean;
}
export type Mark = (key: string) => LineMark | undefined;

/** A line of the card, tagged with its letter where the edit pane marks the card. */
export const Marked = ({
  k,
  mark,
  children,
}: {
  k: string;
  mark?: Mark;
  children: ReactNode;
}) => {
  const m = mark?.(k);
  if (!m) return <>{children}</>;
  return (
    <div className={`${styles.cardMarked} ${m.changed ? styles.cardChanged : ""}`}>
      <span className={styles.cardLetter}>{m.letter}</span>
      <div>{children}</div>
    </div>
  );
};

const Part = ({ b, part, mark }: { b: AmazonBuilding; part: Shown; mark?: Mark }) => {
  // Only the lines that say something the status does not (cardTexts).
  const kept = cardTexts(part);
  const line = (k: CardLine, node: ReactNode) =>
    kept.has(k) ? (
      <Marked k={k} mark={mark}>
        {node}
      </Marked>
    ) : null;
  const title = part.cell.shownAs?.title ?? part.step.title;
  // A step written over on the page reads that value, not the keeper's record: the comment
  // says so, for the agent to leave the record be (AMAZON_STEPS).
  const byHand =
    markOf(useEdit(), b.project_id, cellKeys(part.id)) === "none" ? "" : " (written over by hand)";
  return (
    <div
      className={styles.cardPart}
      {...reviewAttrs("amazon_step", `${b.site_code} · ${title}${byHand}`.slice(0, 200), {
        agent: b.steps[part.id]?.agent ?? "",
        itemId: part.id,
      })}
    >
      <div className={styles.cardHead}>
        <span className={styles.cardTitle}>{title}</span>
        <PartyChip
          owner={part.cell.shownAs?.owner ?? part.step.owner}
          label={part.cell.shownAs ? undefined : part.step.ownerLabel}
        />
      </div>
      {line(
        "status",
        <div className={styles.cardStatus}>
          <i className={TONE_CLASS[part.cell.tone]} /> {part.cell.status}
        </div>,
      )}
      {part.cell.note && line("note", <p>{firstSentence(part.cell.note)}</p>)}
      {part.cell.lines?.length ? line("lines", part.cell.lines.map((l) => <p key={l}>{l}</p>)) : null}
      {part.cell.more &&
        line(
          "more",
          <details className={styles.cardMore}>
            <summary>
              {part.cell.more.label} <Chevron />
            </summary>
            <p>{part.cell.more.text}</p>
          </details>,
        )}
      {proofOf(part.id, part.record) && line("proof", <Proof id={part.id} step={part.record} />)}
      {part.items && line("items", <NeededList b={b} part={{ ...part, items: part.items }} />)}
      {part.line &&
        line("line", <p className={part.means ? styles.cardMeans : undefined}>{part.line}</p>)}
    </div>
  );
};

/** The hover card's body for a cell: the building, then each of its steps. */
export const CellCard = ({
  b,
  parts,
  mark,
}: {
  b: AmazonBuilding;
  parts: Shown[];
  /** The edit pane's letters on each line; the reader's card has none. */
  mark?: Mark;
}) => (
  <>
    <div className={styles.cardLine}>
      {b.site_code} · {b.name}
    </div>
    {parts.map((p) => (
      <Part key={p.id} b={b} part={p} mark={mark} />
    ))}
  </>
);
